<!--
Copyright (c) 2020-2025 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details.
-->

<template>
    <footer v-if="showFooter" id="footer" :class="[ 'footer', 'text-muted', `${themeName.toLowerCase ()}-footer` ]">
        <div :class="[ 'container', 'pearson-footer' ]">
            <ul>
                <span id="professional_assessment_span" v-if="links.about">
                    <li>
                        <a id="professional_assessment"
                           :href="links.about"
                           target="_blank">
                            <span id="about">{{ $t('pages.selfservice.passwordReset.about') }}</span>
                        </a>
                    </li>
                </span>

                <span id="QIInteractive_licensing_assessment_span" v-if="links.license">
                    <li>
                        <a id="QIInteractive_licensing"
                           :href="links.license"
                           target="_blank">
                            <span id="license">{{ $t('pages.selfservice.passwordReset.license') }}</span>
                        </a>
                    </li>
                </span>

                <span id="Terms_span" v-if="links.terms">
                    <li>
                        <a id="Terms"
                           :href="links.terms"
                           target="_blank">
                            <span id="terms">{{ $t('pages.selfservice.passwordReset.terms') }}</span>
                        </a>
                    </li>
                </span>

                <span id="TermsofSaleUse_span" v-if="links.termsofuse">
                    <li>
                        <a id="TermsofSaleUse"
                           :href="links.termsofuse"
                           target="_blank">
                            <span id="termsofuse">{{ termsOfUseLabel }}</span>
                        </a>
                    </li>
                </span>

                <span id="clinical_privacyPolicy_span" v-if="links.privacy">
                    <li>
                        <a id="clinical_privacyPolicy"
                           :href="links.privacy"
                           target="_blank">
                            <span id="privacy">{{ privacyLabel }}</span>
                        </a>
                    </li>
                </span>

                <span id="businessAssociate_addendum_span" v-if="links.businessAssociate">
                    <li>
                        <a id="businessAssociate_addendum"
                           :href="links.businessAssociate"
                           target="_blank">
                            <span id="bussiness">{{ $t('pages.selfservice.passwordReset.businessAssociate') }}</span>
                        </a>
                    </li>
                </span>

                <span id="doNotSell_span" v-if="links.doNotSell">
                    <li>
                        <a id="doNotSell"
                           :href="links.doNotSell"
                           target="_blank">
                            <span id="doNotSell">{{ $t('pages.selfservice.passwordReset.doNotSell') }}</span>
                        </a>
                    </li>
                </span>

                <span id="modernSlavery_span" v-if="links.modernSlavery">
                    <li>
                        <a id="modernSlavery"
                           :href="links.modernSlavery"
                           target="_blank">
                            <span id="modernSlavery">{{ $t('pages.selfservice.passwordReset.modernSlavery') }}</span>
                        </a>
                    </li>
                </span>

                <span id="accessibility_span" v-if="links.accessibility">
                    <li>
                        <a id="accessibility"
                           :href="links.accessibility"
                           target="_blank">
                            <span id="accessibility">{{ $t('pages.selfservice.passwordReset.accessibility') }}</span>
                        </a>
                    </li>
                </span>

                <span id="patent_notice_span" v-if="links.patentNotice && showPatentNoticeLink">
                    <li>
                        <a id="patent_notice"
                           :href="links.patentNotice"
                           target="_blank">
                            <span id="patent_notice">{{ patentNoticeLabel }}</span>
                        </a>
                    </li>
                </span>

                <span id="Contact_span" v-if="links.contact">
                    <li>
                        <a id="contact_addendum"
                           :href="links.contact"
                           target="_blank">
                            <span id="contact">{{ contactLabel }}</span>
                        </a>
                    </li>
                </span>

                <span id="Permissions_span" v-if="links.permissions">
                    <li>
                        <a id="permissions" style="text-decoration: none;"
                           :href="links.permissions"
                           target="_blank">
                            <span id="permissions">{{ $t('pages.selfservice.passwordReset.permissions') }}</span>
                        </a>
                    </li>
                </span>
                <span id="Support_span" v-if="links.support">
                    <li>
                        <a id="support" style="text-decoration: none;"
                           :href="links.support"
                           target="_blank">
                            <span id="support">{{ $t('pages.selfservice.passwordReset.support') }}</span>
                        </a>
                    </li>
                </span>
                <span id="Cookies_span" v-if="links.cookies && !isPcasClient">
                    <li>
                        <a id="cookies"
                           :href="links.cookies"
                           target="_blank">
                            <span>{{ $t('pages.selfservice.passwordReset.cookies') }}</span>
                        </a>
                    </li>
                </span>
                <span id="Changecookieconsent_span" v-if="!isPcasClient && links.cookieConsent">
                    <li>
                        <a id="changecookieconsent" style="text-decoration: none;"
                           :href="links.cookieConsent"
                           target="_blank">
                            <span id="changecookieconsent">{{ $t('pages.selfservice.passwordReset.changecookieconsent') }}</span>
                        </a>
                    </li>
                </span>
                <span id="Cookies_pcas_span" v-if="isPcasClient">
                    <li>
                        <a id="cookies_pcas"
                           :href="pcasCookiePolicyHref"
                           @click.prevent="handleCookieLinkClick"
                           target="_blank">
                            <span>{{ $t('pages.selfservice.passwordReset.cookies') }}</span>
                        </a>
                    </li>
                </span>

                <span id="copyright">
                    <li v-if="themeName === 'GED'"
                        id="ged-manager-footer"
                        class="ged-manager-copyright-text"
                    >
                        {{ currentYear }}©{{ ui.copyrightText.companyName }}{{ ui.copyrightText.allRightsReserved }}
                    </li>
                    <li v-else>
                        {{ copyrightLabel }}<span id="copyright-year">{{ currentYear }}</span>
                        <template v-if="ui.copyrightText && ui.copyrightText.companyName">
                            <template v-if="ui.showPearsonLink !== false">
                                <a :href="ui.pearsonLinkHref || 'https://www.pearson.com'" id="copyright-hyperlink" target="_blank"
                                    title="Opens in a new tab or window">&nbsp;{{ ui.copyrightText.companyName }}&nbsp;</a>{{ allRightsReservedText }}
                            </template>
                            <template v-else>&nbsp;{{ ui.copyrightText.companyName }}{{ allRightsReservedText }}</template>
                        </template>
                        <template v-else>
                            <template v-if="ui.showPearsonLink !== false">
                                <a :href="ui.pearsonLinkHref || 'https://www.pearson.com'" id="copyright-hyperlink" target="_blank"
                                    title="Opens in a new tab or window">&nbsp;Pearson</a>&nbsp;{{ allRightsReservedText }}
                            </template>
                            <template v-else>&nbsp;Pearson&nbsp;{{ allRightsReservedText }}</template>
                        </template>
                    </li>
                </span>
            </ul>
        </div>
    </footer>
</template>

<script>
/**
 * @description Pearson Footer component for use across various pages
 */
export default {
    name: 'PearsonFooter',
    props: {
        themeName: {
            type: String,
            default: 'BASE'
        },
        showFooter: {
            type: Boolean,
            default: false
        },
        links: {
            type: Object,
            default: () => ({
                about: '',
                license: '',
                terms: '',
                termsofuse: '',
                privacy: '',
                businessAssociate: '',
                contact: '',
                accessibility: '',
                modernSlavery: '',
                doNotSell: '',
                patentNotice: '',
                cookieConsent: '',
                permissions: '',
                support: '',
                cookies: ''
            })
        },
        ui: {
            type: Object,
            default: () => ({
                showPearsonLink: true,
                copyrightYearInRange: false,
                pearsonLinkHref: '',
                copyrightText: {
                    companyName: 'Pearson',
                    allRightsReserved: ' All rights reserved.'
                }
            })
        }
    },
    data () {
        return {};
    },
    computed: {
        // Check if current client is a PCAS client
        isPcasClient () {
            const pcasClients = [
                'PCASUS-Client',
                'PCASCA-Client',
                'PCASNL-Client',
                'PCASUK-Client',
                'PCASAN-Client',
                'PCASAP-Client',
                'PCASES-Client'
            ];

            // Get clientId from multiple possible sources
            const getClientId = () => {
                // Try to get from URL - check both direct and encoded parameters
                const url = window.location.href;

                // First try direct client_id parameter
                let clientIdMatch = url.match(/[?&]client_id=([^&#]*)/i) || url.match(/[?&]clientId=([^&#]*)/i);
                if (clientIdMatch) {
                    const clientId = decodeURIComponent(clientIdMatch[1]);
                    if (clientId && clientId !== '') {
                        return clientId;
                    }
                }

                // If not found, try to extract from encoded goto parameter (could be multiple levels deep)
                const gotoMatch = url.match(/[?&]goto=([^&#]*)/i);
                if (gotoMatch) {
                    let gotoUrl = decodeURIComponent(gotoMatch[1]);
                    // Decode multiple times to handle nested encoding
                    for (let i = 0; i < 5; i++) {
                        const nestedMatch = gotoUrl.match(/client_id[=%]([^&%#]*)/i);
                        if (nestedMatch) {
                            const clientId = decodeURIComponent(nestedMatch[1]);
                            if (pcasClients.includes(clientId)) {
                                return clientId;
                            }
                        }
                        // Try decoding again
                        try {
                            const decoded = decodeURIComponent(gotoUrl);
                            if (decoded === gotoUrl) break; // No more decoding needed
                            gotoUrl = decoded;
                        } catch (e) {
                            break;
                        }
                    }
                }

                // Fallback to localStorage
                return localStorage.getItem('clientId') || '';
            };

            const clientId = getClientId();
            console.log('PearsonFooter - isPcasClient check for clientId:', clientId, 'isPcas:', pcasClients.includes(clientId));
            return pcasClients.includes(clientId);
        },
        // Calculate copyright year based on copyrightYearInRange flag
        currentYear () {
            const year = new Date().getFullYear();
            return this.ui.copyrightYearInRange ? `1996-${year}` : year;
        },
        // Determine which contact label to use based on theme
        contactLabel () {
            // PCAS clients should use "Contact us" translation
            const pcasThemes = ['PCAS'];
            const isPcasTheme = pcasThemes.includes(this.themeName);
            const currentLocale = this.$i18n.locale;

            // For PCAS clients with fr-CA locale, use special translation
            if (isPcasTheme && currentLocale === 'fr-CA') {
                return this.$t('pages.selfservice.passwordReset.contactPcas');
            }

            return isPcasTheme
                ? this.$t('pages.selfservice.passwordReset.contactUs')
                : this.$t('pages.selfservice.passwordReset.contact');
        },
        // Determine which privacy label to use based on theme and locale
        privacyLabel () {
            const pcasThemes = ['PCAS'];
            const isPcasTheme = pcasThemes.includes(this.themeName);
            const currentLocale = this.$i18n.locale;

            // For PCAS clients with fr-CA locale, use special translation
            if (isPcasTheme && currentLocale === 'fr-CA') {
                return this.$t('pages.selfservice.passwordReset.privacyPcas');
            }

            return this.$t('pages.selfservice.passwordReset.privacy');
        },
        // Hide patent notice link for PCAS clients when locale is "en" (UK only) or contains "es" (e.g. es, es-LA)
        showPatentNoticeLink () {
            const locale = this.$i18n.locale;
            if (locale === 'en' && this.isPcasClient) return false;
            if (locale && locale.toLowerCase().startsWith('es') && this.isPcasClient) return false;
            return true;
        },
        // Determine which patent notice label to use based on theme and locale
        patentNoticeLabel () {
            const pcasThemes = ['PCAS'];
            const isPcasTheme = pcasThemes.includes(this.themeName);
            const currentLocale = this.$i18n.locale;

            // For PCAS clients with fr-CA locale, use special translation
            if (isPcasTheme && currentLocale === 'fr-CA') {
                return this.$t('pages.selfservice.passwordReset.patentNoticePcas');
            }

            return this.$t('pages.selfservice.passwordReset.patentNotice');
        },
        // Determine which terms of use label to use based on theme and locale
        termsOfUseLabel () {
            const pcasThemes = ['PCAS'];
            const isPcasTheme = pcasThemes.includes(this.themeName);
            const currentLocale = this.$i18n.locale;

            // For PCAS clients with nl locale, use special translation
            if (isPcasTheme && currentLocale === 'nl') {
                return this.$t('pages.selfservice.passwordReset.termsofusePcas');
            }

            return this.$t('pages.selfservice.passwordReset.termsofuse');
        },
        // Determine which "All rights reserved" text to use based on theme and locale
        allRightsReservedText () {
            const pcasThemes = ['PCAS'];
            const isPcasTheme = pcasThemes.includes(this.themeName);
            const currentLocale = this.$i18n.locale;

            // For French locales (fr, fr-CA, fr-FR), use translated version
            if (currentLocale && currentLocale.toLowerCase().startsWith('fr')) {
                return ' ' + this.$t('pages.selfservice.passwordReset.allRightsReserved');
            }

            return isPcasTheme
                ? this.$t('pages.selfservice.passwordReset.allRightsReserved')
                : ' All rights reserved.';
        },
        // Copyright label: use translation for "es" locale, literal "Copyright ©" for all others
        copyrightLabel () {
            return this.$i18n.locale.startsWith('es')
                ? this.$t('pages.selfservice.passwordReset.copyright')
                : 'Copyright © ';
        },
        // PCAS cookie policy URL with locale from URL (e.g. ?locale=en-US)
        pcasCookiePolicyHref () {
            const base = 'https://www.pearson.com/en-us/legal-information/cookie-policy.html';
            const locale = this.$i18n.locale || 'en-US';
            return `${base}?locale=${encodeURIComponent(locale)}`;
        }
    },
    methods: {
        handleCookieLinkClick (event) {
            // Trigger OneTrust cookie banner
            if (typeof window.OneTrust !== 'undefined' && typeof window.OneTrust.ToggleInfoDisplay === 'function') {
                window.OneTrust.ToggleInfoDisplay();
            } else if (typeof window.Optanon !== 'undefined' && typeof window.Optanon.ToggleInfoDisplay === 'function') {
                window.Optanon.ToggleInfoDisplay();
            }
        }
    },
    mounted () {
        this.$emit('mounted');
    }
};
</script>

<style scoped lang="scss">
/* Styles are imported from pearson-common.css at the page level */
</style>
